import { isMembershipLine } from '@notifi/shared-types/membership-line';

interface ProposalOptionPricingLine {
  type?: string | null;
  taxCategory?: string | null;
  lineTotal?: number | null;
  lineSubtotal?: number | null;
  isSelected?: boolean | null;
}

export interface ProposalOptionPricing {
  discountType?: 'PERCENT' | 'FLAT' | null;
  discountValue?: number | null;
  /**
   * "Require deposit before scheduling" — enforcement, not the quote. Accepted so an
   * option can be passed whole, and deliberately not read by the amount math;
   * see {@link proposalOptionDepositAmountOn}.
   */
  depositRequired?: boolean;
  depositType?: 'PERCENT' | 'FLAT' | null;
  depositValue?: number | null;
  subtotal: number;
  total: number;
  /**
   * The option's own lines. Needed to answer what its deposit is charged
   * against — see {@link getProposalOptionDepositEligibleTotal}. Absent (a
   * caller with no lines to hand) falls back to the option total, which is what
   * the base reduces to for an option carrying no membership.
   */
  lineItems?: ProposalOptionPricingLine[] | null;
}

export const OPTION_COLORS = ['#64748b', '#6366f1', '#f59e0b', '#10b981', '#ef4444'];

/**
 * NOT-604 — the base a deposit on THIS option is charged against: the option's
 * own total minus any SELECTED membership line inside it, because a recurring
 * subscription is not deposit-eligible work.
 *
 * Byte-for-byte the server's `depositEligibleTotal(option.total,
 * option.lineItems)` — the figure `ProposalsService.materializeEstimate` charges
 * the born estimate's deposit on, and the same base the option CARD previews
 * through `computeProposalOptionPreview`. Three surfaces, one number: a screen
 * that clamps a deposit against a different base than the thing charging it is
 * the whole defect this module was fixed for, and leaving the detail breakdown
 * on the raw total was that defect one surface over.
 */
export function getProposalOptionDepositEligibleTotal(option: ProposalOptionPricing): number {
  if (!Array.isArray(option.lineItems)) return option.total;
  const membershipLineTotal = option.lineItems.reduce((sum, line) => {
    if (!line || line.isSelected === false || !isMembershipLine(line)) return sum;
    const contribution = Number(line.lineTotal ?? line.lineSubtotal ?? 0);
    return sum + (Number.isFinite(contribution) ? contribution : 0);
  }, 0);
  return Math.max(0, Math.round((option.total - membershipLineTotal) * 100) / 100);
}

/**
 * Both amounts CLAMP the way the server's pricing and deposit engines do, so a
 * stored oversized value — one written before the option refused it, or by an
 * API caller — previews the figure the customer's page and the PDF actually
 * print, instead of an absurd one only this screen believes.
 *
 * A percent is capped at 100, a flat amount at the base it is charged against
 * (the pre-document-discount subtotal for a discount, the deposit-eligible
 * total for a deposit). A proposal has one of these per OPTION, so every base
 * here is that option's own — never an aggregate across Good/Better/Best.
 */
export function getProposalOptionDiscountAmount(option: ProposalOptionPricing): number {
  if (option.discountType === 'PERCENT') {
    return (option.subtotal * Math.min(100, Number(option.discountValue ?? 0))) / 100;
  }
  if (option.discountType === 'FLAT') {
    return Math.min(Number(option.discountValue ?? 0), option.subtotal);
  }
  return 0;
}

/**
 * The deposit charged on an explicit BASE — for a surface that already knows
 * what the customer is currently choosing (the public option card recomputes
 * the base when a join-&-save membership is ticked, because the member rate
 * moves the deposit-eligible work with it). Everyone else wants
 * {@link getProposalOptionDepositAmount}, which reads the option's own base.
 *
 * Mirrors `DepositEngineService.calculateDocumentAmount` + `calculate`, which
 * is what `ProposalsService.materializeEstimate` charges the born estimate's
 * deposit with:
 *  - PERCENT: clamped to 0–100, applied to the base;
 *  - FLAT: floored at 0, capped at the base;
 *  - no type: nothing — the engine's typeless branch has no stored amount to
 *    fall back to on a proposal option, so it resolves to 0.
 *
 * `depositRequired` is deliberately NOT a gate here, and this is the whole of
 * the bug it was: the flag is the "Require deposit before scheduling" switch, which
 * governs whether the money is COLLECTED, not whether a deposit was quoted.
 * The switch defaults OFF and only appears once an amount is typed, so an
 * author who filled in "Deposit 24,000" and left it alone had a deposit the
 * builder previewed (`OptionCard` gates on the amount) and the born estimate
 * charged (`calculateDocumentAmount` never reads the flag) — but that every
 * proposal-side breakdown silently dropped. Estimates print their deposit on
 * the same rule (`EstimatePDF`, the public quote's `selectedDepositAmount`);
 * this was the one implementation in the codebase that read the flag.
 */
export function proposalOptionDepositAmountOn(
  option: Pick<ProposalOptionPricing, 'depositType' | 'depositValue'>,
  base: number,
): number {
  const value = Number(option.depositValue ?? 0);
  if (!Number.isFinite(value) || value <= 0) return 0;
  const cap = Math.max(0, Number.isFinite(base) ? base : 0);
  if (option.depositType === 'PERCENT') {
    return Math.round(cap * (Math.min(100, value) / 100) * 100) / 100;
  }
  if (option.depositType === 'FLAT') {
    return Math.min(value, cap);
  }
  // A typeless deposit is never charged, so it is never drawn.
  return 0;
}

/** The deposit this option quotes, charged on its own deposit-eligible total. */
export function getProposalOptionDepositAmount(option: ProposalOptionPricing): number {
  return proposalOptionDepositAmountOn(option, getProposalOptionDepositEligibleTotal(option));
}
